"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Users, Fuel, Gauge, Settings2, Info } from "lucide-react";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Button } from "@/components/ui/button";
const wp_query_params = {
  post_type: "product",
  per_page: 2,
  status: "publish",
  orderby: "menu_order",
  order: "asc"
};
const wp_query = new WP_Query(wp_query_params);
function RentalUnitsConsumer() {
  const {
    products,
    loading,
    error,
    refetch
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="h-[420px] w-full animate-pulse rounded-xl bg-muted/20" />)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="mb-4 text-muted-foreground">
          Unable to load rental fleet at this time.
        </p>
        <Button onClick={refetch} variant="outline">
          Try Again
        </Button>
      </div>;
  }
  if (products.length === 0) {
    return <div className="py-12 text-center">
        <p className="text-lg text-muted-foreground">
          No vehicles currently available.
        </p>
      </div>;
  }
  return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
      {products.map((product, i) => {
      const specs = [];
      if (product.customFields?.passengers) {
        specs.push({
          icon: Users,
          label: `${product.customFields.passengers} Seats`
        });
      } else if (product.tags.some(t => t.name.includes("Seat"))) {
        const tag = product.tags.find(t => t.name.includes("Seat"));
        if (tag) specs.push({
          icon: Users,
          label: tag.name
        });
      }
      if (product.customFields?.transmission) {
        specs.push({
          icon: Settings2,
          label: product.customFields.transmission
        });
      } else if (product.tags.some(t => t.name.includes("Auto") || t.name.includes("Manual"))) {
        const tag = product.tags.find(t => t.name.includes("Auto") || t.name.includes("Manual"));
        if (tag) specs.push({
          icon: Settings2,
          label: tag.name
        });
      }
      if (product.customFields?.fuel) {
        specs.push({
          icon: Fuel,
          label: product.customFields.fuel
        });
      } else if (product.tags.some(t => t.name.includes("Diesel") || t.name.includes("Petrol") || t.name.includes("Electric") || t.name.includes("Hybrid"))) {
        const tag = product.tags.find(t => t.name.includes("Diesel") || t.name.includes("Petrol") || t.name.includes("Electric") || t.name.includes("Hybrid"));
        if (tag) specs.push({
          icon: Fuel,
          label: tag.name
        });
      }
      if (specs.length === 0 && product.tags.length > 0) {
        product.tags.slice(0, 3).forEach(tag => {
          specs.push({
            icon: Info,
            label: tag.name
          });
        });
      }
      const allowedLabels = ["Seats", "Transmission", "Fuel"];
      const hasNonStandardSpecs = specs.some(spec => !allowedLabels.includes(spec.label));
      if (specs.length === 0 || hasNonStandardSpecs) {
        specs.length = 0;
        specs.push({
          icon: Users,
          label: "Seats"
        });
        specs.push({
          icon: Settings2,
          label: "Transmission"
        });
        specs.push({
          icon: Fuel,
          label: "Fuel"
        });
      }
      return <Card key={product.id} data-index={i} className="group relative flex flex-col overflow-hidden border-border/50 bg-card transition-all duration-300 hover:border-primary/50 hover:shadow-lg">
            {}
            <div className="relative aspect-[4/3] overflow-hidden bg-muted">
              {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" /> : <div className="flex h-full w-full items-center justify-center bg-secondary/20 text-muted-foreground">
                  <span className="text-sm">No Image Available</span>
                </div>}
              
              {}
              <div className="absolute top-4 right-4 z-10">
                <Badge variant="default" className="bg-primary text-primary-foreground px-3 py-1.5 text-sm font-bold shadow-md backdrop-blur-md">
                  From £{product.price}/day
                </Badge>
              </div>
            </div>

            <CardHeader className="pb-2">
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <CardTitle className="text-xl font-bold tracking-tight text-card-foreground">
                    <Link to={product.link} className="hover:text-primary transition-colors">
                      {product.title}
                    </Link>
                  </CardTitle>
                  <CardDescription className="line-clamp-1 text-muted-foreground">
                    {product.categories?.[0]?.name || "Rental Vehicle"}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>

            <CardContent className="flex-grow space-y-4 py-2">
              {}
              <ToggleGroup type="multiple" className="w-full justify-start gap-2 flex-wrap">
                  {specs.map((spec, idx) => <ToggleGroupItem key={idx} value={spec.label} className="h-8 px-2.5 text-xs font-medium data-[state=on]:bg-secondary data-[state=on]:text-secondary-foreground pointer-events-none border border-border/50 bg-secondary/10 hover:bg-secondary/20" aria-label={spec.label}>
                      <spec.icon className="mr-1.5 h-3.5 w-3.5" />
                      {spec.label}
                    </ToggleGroupItem>)}
                </ToggleGroup>

              {}
              <div className="line-clamp-2 text-sm text-muted-foreground/80 [&_p]:m-0" dangerouslySetInnerHTML={{
            __html: product.short_description || "No description available."
          }} />
            </CardContent>

            <CardFooter className="pt-4 pb-6">
              <div className="flex w-full flex-col gap-3">
                <AddToCartButton productId={product.id} variant="default" text="Rent Now" className="w-full" buttonClassName="w-full bg-primary text-primary-foreground font-bold hover:bg-primary/90 h-11 text-base shadow-md transition-all hover:shadow-primary/20" showQuantity={false} />
                <Link to={product.link} className="w-full">
                  <Button variant="outline" className="w-full border-border/50 hover:bg-secondary/50 hover:text-foreground">
                    View Details
                  </Button>
                </Link>
              </div>
            </CardFooter>
          </Card>;
    })}
    </div>;
}
export default function AvailableRentalUnits() {
  return <section id="available-rental-units" className="dark bg-background text-foreground py-16 sm:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-12 flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div className="max-w-2xl space-y-4">
            <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl">
              Available Rental Units
            </h2>
            <p className="text-lg text-muted-foreground">Choose from our premium fleet of vehicles. Whether you need a compact car for city driving or a spacious van for business, we have the perfect match for your journey. Drive your way, get a better day.</p>
          </div>
          <div className="hidden md:block">
            <Link to="#">
              <Button variant="link" className="text-primary text-lg font-medium hover:text-primary/80 p-0 h-auto">
                View Full Fleet &rarr;
              </Button>
            </Link>
          </div>
        </div>

        <EcommerceProductsProvider wp_query={{
        post_type: "product",
        per_page: 2,
        status: ["publish"],
        orderby: "menu_order",
        order: "asc"
      }} className="w-full">
          <RentalUnitsConsumer />
        </EcommerceProductsProvider>

        <div className="mt-12 flex justify-center md:hidden">
          <Link to="#">
            <Button variant="outline" className="w-full sm:w-auto">
              View Full Fleet
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}